import AppRoute from 'src/router/app-route';
import ExternalRoute from 'src/router/external-route';
import { Route } from 'src/router/routes';
import { getXprtJobsOrigin } from 'src/utils/xprt-jobs-route-util';

/**
 * Builds the primary navigation, which reads the same on every page so moving between the market and the account pages
 * never takes the rest of the site out of reach. A logged-in member gets My Market in place of Home, which the logo
 * still leads to.
 *
 * @param {Object} options - Navigation context.
 * @param {boolean} options.isAuthenticated - Whether the visitor is logged in.
 * @returns {Object[]} - Ordered navigation links.
 */
export function buildPrimaryNavigation(options) {
    const { isAuthenticated } = options;
    return [
        buildLeadingLink(isAuthenticated),
        {
            title: 'Market',
            icon: 'sym_r_monitoring',
            route: AppRoute.build(Route.MarketOverview),
            groupIndex: 0
        },
        {
            title: 'Methodology',
            icon: 'sym_r_science',
            route: AppRoute.build(Route.Methodology),
            groupIndex: 0
        },
        {
            title: 'Career profile',
            icon: 'sym_r_account_box',
            route: AppRoute.build(Route.ManageUserProfile),
            groupIndex: 0
        },
        {
            title: 'Tech jobs',
            icon: 'sym_r_north_east',
            route: ExternalRoute.build(getXprtJobsOrigin()),
            groupIndex: 0
        }
    ];
}

function buildLeadingLink(isAuthenticated) {
    if (isAuthenticated) {
        return {
            title: 'My Market',
            icon: 'sym_r_bookmark',
            route: AppRoute.build(Route.Dashboard),
            groupIndex: 0,
            exactRoute: true
        };
    }
    return {
        title: 'Home',
        icon: 'sym_r_home',
        route: AppRoute.build(Route.Home),
        groupIndex: 0,
        exactRoute: true
    };
}

/**
 * Builds the account settings a logged-in member reaches from the small-screen menu. On wider screens the user menu
 * holds them, so they stay out of the primary navigation.
 *
 * @param {Object} options - Navigation context.
 * @param {boolean} options.hasProfileLinkAccess - Whether the member's role can share profile links.
 * @returns {Object[]} - Ordered account links.
 */
export function buildAccountNavigation(options) {
    const { hasProfileLinkAccess } = options;
    const profileLinks = {
        title: 'Profile links',
        icon: 'sym_r_captive_portal',
        route: AppRoute.build(Route.ManageProfileLinks),
        groupIndex: 1
    };
    return [
        ...(hasProfileLinkAccess ? [profileLinks] : []),
        {
            title: 'Invitations',
            icon: 'sym_r_share',
            route: AppRoute.build(Route.ManageInviteLinks),
            groupIndex: 1
        },
        {
            title: 'Account',
            icon: 'sym_r_manage_accounts',
            route: AppRoute.build(Route.ManageAccount),
            groupIndex: 1
        },
        {
            title: 'Security',
            icon: 'sym_r_security',
            route: AppRoute.build(Route.ManageAuth),
            groupIndex: 1
        }
    ];
}
